import React, { useMemo } from 'react';
import {
  buildChildrenMap,
  buildCorrelationCountMap,
  countMatchesInSubtree,
  getFilteredNodeIds,
} from './standardsTreeUtils';
import { StandardNode, StateOption } from './types';
import StandardsBrowserContent from './StandardsBrowserContent';
import StandardsToolbar from './StandardsToolbar';

interface StandardsBrowserPanelProps {
  expandedGrades: Set<string>;
  expandedNodes: Set<number>;
  grade: string;
  hasData: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  searchQuery: string;
  standards: StandardNode[];
  stateId: number | null;
  stateName: string | null;
  stateOptions: StateOption[];
  onExpandToggle: () => void;
  onGradeChange: (grade: string) => void;
  onSearchChange: (query: string) => void;
  onStateChange: (stateId: number | null) => void;
  onToggleGrade: (grade: string) => void;
  onToggleNode: (id: number) => void;
}

const StandardsBrowserPanel = ({
  expandedGrades,
  expandedNodes,
  grade,
  hasData,
  isExpanded,
  isLoading,
  searchQuery,
  standards,
  stateId,
  stateName,
  stateOptions,
  onExpandToggle,
  onGradeChange,
  onSearchChange,
  onStateChange,
  onToggleGrade,
  onToggleNode,
}: StandardsBrowserPanelProps) => {
  const showCentered = !stateName || isLoading || standards.length === 0;
  const isSearching = searchQuery.trim().length > 0 && hasData;
  const childrenMap = useMemo(() => buildChildrenMap(standards), [standards]);
  const correlationCountMap = useMemo(() => buildCorrelationCountMap(childrenMap), [childrenMap]);
  const showCorrelationCounts = useMemo(
    () => standards.some(s => s.correlation_count != null),
    [standards],
  );
  const searchResult = useMemo(
    () => (isSearching
      ? getFilteredNodeIds(standards, searchQuery)
      : { matchIds: new Set<number>(), visibleIds: new Set<number>() }),
    [standards, searchQuery, isSearching],
  );
  const matchCount = useMemo(() => {
    if (!isSearching) return 0;
    if (!grade) return searchResult.matchIds.size;
    const roots = (childrenMap.get(null) ?? []).filter(r => (r.grade ?? 'K') === grade);
    return roots.reduce(
      (acc, r) => acc + countMatchesInSubtree(r.id, childrenMap, searchResult.matchIds),
      0,
    );
  }, [isSearching, grade, childrenMap, searchResult]);

  return (
    <div
      className="tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-light-grey tw-mt-4"
    >
      <StandardsToolbar
        grade={grade}
        hasData={hasData}
        isExpanded={isExpanded}
        matchCount={isSearching ? matchCount : undefined}
        searchQuery={searchQuery}
        stateId={stateId}
        stateOptions={stateOptions}
        onExpandToggle={onExpandToggle}
        onGradeChange={onGradeChange}
        onSearchChange={onSearchChange}
        onStateChange={onStateChange}
      />
      <div className={showCentered
        ? 'tw-bg-white tw-p-4 tw-flex tw-items-center tw-justify-center tw-py-16 tw-text-sm tw-text-dark-grey'
        : 'tw-bg-white'}
      >
        {isSearching && (
          <div className="tw-px-3 tw-py-1.5 tw-text-xs tw-text-dark-grey tw-flex tw-items-center tw-gap-1">
            <span className="tw-px-2.5 tw-h-3" style={{ backgroundColor: '#FFF8DD' }}>&nbsp;</span>
            Search match
          </div>
        )}
        <StandardsBrowserContent
          childrenMap={childrenMap}
          correlationCountMap={correlationCountMap}
          expandedGrades={expandedGrades}
          expandedNodes={expandedNodes}
          gradeFilter={grade}
          isLoading={isLoading}
          searchQuery={searchQuery}
          searchResult={searchResult}
          showCorrelationCounts={showCorrelationCounts}
          standards={standards}
          stateName={stateName}
          onClearSearch={() => onSearchChange('')}
          onToggleGrade={onToggleGrade}
          onToggleNode={onToggleNode}
        />
      </div>
    </div>
  );
};

export default StandardsBrowserPanel;
